import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShoppingBag, X } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, PageHead, Section, TONES } from "@/components/account/Shell";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import WriteReview from "@/components/account/WriteReview";

const TONE = "#1a1a1a";
const LABELS = {
    pending_payment: "Awaiting payment", paid: "Paid", in_production: "In production",
    ready: "Ready for pickup", completed: "Completed", cancelled: "Cancelled",
};
const STYLE = {
    pending_payment: "border-[#b54708] bg-[#fffaeb] text-[#b54708]",
    paid: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    in_production: "border-[#1a56db] bg-[#eff4ff] text-[#1a56db]",
    ready: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    completed: "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]",
    cancelled: "border-[#b42318] bg-[#fef3f2] text-[#b42318]",
};

/** The buyer side of the same account: what you bought, and the passes you pay for. */
export default function Purchases() {
    const { addItem, setOpen } = useCart();
    const [orders, setOrders] = useState(null);
    const [mem, setMem] = useState(null);
    const [vendor, setVendor] = useState(null);
    const [rev, setRev] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadReviews = () => api.get("/reviews/mine").then(({ data }) => setRev(data)).catch(() => setRev(null));

    useEffect(() => {
        api.get("/orders/mine").then(({ data }) => setOrders(data.orders)).catch(() => setOrders([]));
        api.get("/membership/me").then(({ data }) => setMem(data.membership)).catch(() => { });
        loadReviews();
        api.get("/mall/me").then(({ data }) => setVendor(data.vendor && {
            ...data.vendor,
            price_month: data.vendor.price_month ?? data.plan?.price_month ?? 0,
            price_year: data.vendor.price_year ?? data.plan?.price_year,
        })).catch(() => { });
    }, []);

    const cancelSub = async (kind) => {
        setBusy(true);
        try {
            const { data } = await api.post("/payments/subscription/cancel", { kind });
            toast.success(data.note);
            if (kind === "gang") setMem((m) => ({ ...m, status: "cancelled" }));
            else setVendor((v) => ({ ...v, plan: "kiosk", plan_name: "Starter", price_month: 0, status: "active" }));
        } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
        finally { setBusy(false); }
    };

    const reorder = (order) => {
        order.items.forEach((it) => addItem({
            product_slug: it.product_slug, product_name: it.product_name, category: it.category,
            selections: it.selections, artwork: it.artwork || [], quantity: it.quantity,
            unit_price: it.unit_price, line_total: it.line_total, image: "",
            summary: Object.values(it.selections || {}).filter((v) => typeof v === "string").join(" · "),
        }));
        toast.success("Added to cart");
        setOpen(true);
    };

    return (
        <BackOffice>
            <div data-testid="purchases-page">
                <PageHead kicker="My account" tone={TONE} title="Purchases & passes" testid="purchases-head"
                    sub="Every order you placed on 13thav, and the two things you can subscribe to." />

                <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="subscriptions-panel">
                    {[
                        ["gang", "Wholesale pass", mem, "/wholesale", "Get a Wholesale pass",
                            "Save on every order you place on 13thav — Bronze $5, Silver $50, Gold $150."],
                        ["mall", "Shop plan", vendor, "/account/sell", "Pick a plan",
                            "Sell on 13thav. Starter is $9 a month, Storefront is $29, Pro Shop is $79 and your own private site is $199."],
                    ].map(([kind, label, sub, to, cta, blurb]) => {
                        const live = sub && ["trial", "active"].includes(sub.status);
                        return (
                            <Section key={kind} title={label} tone={TONE} testid={`sub-card-${kind}`}
                                note={live ? (sub.status === "trial" ? "free trial" : "active") : "not subscribed"}>
                                {live ? (
                                    <>
                                        <p className="font-display text-base font-black text-zinc-900">{sub.tier_name || sub.plan_name}</p>
                                        <p className="mt-0.5 text-sm text-zinc-800">
                                            {sub.status === "trial"
                                                ? `Free through ${new Date(sub.trial_ends_at).toLocaleDateString()}, then `
                                                : "Renews at "}
                                            ${sub.interval === "yearly" ? (sub.price_year ?? sub.price_month * 5) : sub.price_month}/{sub.interval === "yearly" ? "yr" : "mo"}
                                            {sub.price_month === 0 && " — free forever"}
                                        </p>
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            <Link to={to}><Btn testid={`sub-manage-${kind}`}>Change plan</Btn></Link>
                                            {sub.price_month > 0 && (
                                                <Btn onClick={() => cancelSub(kind)} disabled={busy} testid={`sub-cancel-${kind}`}>
                                                    {busy ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />} Cancel
                                                </Btn>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm text-zinc-800">{blurb}</p>
                                        <Link to={to} className="mt-2 inline-block">
                                            <Btn tone="solid" testid={`sub-get-${kind}`}>{cta}</Btn>
                                        </Link>
                                    </>
                                )}
                            </Section>
                        );
                    })}
                </div>

                <div className="mt-3">
                    <WriteReview jobs={rev?.can_review} written={rev?.written} onDone={loadReviews} />
                </div>

                <div className="mt-3">
                    {orders === null ? (
                        <div className="flex min-h-[20vh] items-center justify-center">
                            <Loader2 className="animate-spin text-zinc-400" />
                        </div>
                    ) : orders.length === 0 ? (
                        <Section title="Your orders" tone={TONE} testid="purchases-empty">
                            <div className="py-6 text-center">
                                <ShoppingBag size={26} className="mx-auto text-zinc-400" />
                                <p className="mt-2 text-sm text-zinc-800">No orders yet.</p>
                                <Link to="/shirts/white" className="mt-2.5 inline-block">
                                    <Btn tone="solid" testid="purchases-start">Start an order</Btn>
                                </Link>
                            </div>
                        </Section>
                    ) : (
                        <Section title="Your orders" tone={TONE} pad={false} testid="purchases-orders"
                            note={`${orders.length} order${orders.length === 1 ? "" : "s"} · one-tap reorder`}>
                            {orders.map((o) => (
                                <div key={o.id} data-testid={`order-${o.order_number}`}
                                    className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-black/10 px-2.5 py-2 first:border-t-0">
                                    <span className="font-mono text-sm font-bold text-zinc-900">{o.order_number}</span>
                                    <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-zinc-700">
                                        {new Date(o.created_at).toLocaleDateString()}
                                    </span>
                                    <span className={`rounded border px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] ${STYLE[o.status] || STYLE.completed}`}>
                                        {LABELS[o.status]}
                                    </span>
                                    <span className="min-w-[160px] flex-1 truncate text-sm text-zinc-800">
                                        {o.items.map((i) => i.product_name).join(", ")}
                                    </span>
                                    <span className="font-mono text-sm font-bold tabular-nums text-zinc-900">${o.total.toFixed(2)}</span>
                                    <Btn onClick={() => reorder(o)} testid="reorder-button"><RefreshCw size={10} /> Reorder</Btn>
                                </div>
                            ))}
                        </Section>
                    )}
                </div>
            </div>
        </BackOffice>
    );
}
